import React from "react";

import PageShell from "@/components/site/PageShell";
import PricingCards from "@/components/site/PricingCards";
import RegionPicker from "@/components/site/RegionPicker";
import { useRegion } from "@/contexts/RegionContext";

export default function Pricing() {
    const { currency, regionData, detectionSource } = useRegion();
    const isFallback = detectionSource === "fallback";
    const caption =
        (isFallback ? "Indicative · " : "") +
        `Pricing shown in ${currency}. Switch region to view local pricing.`;

    return (
        <PageShell testid="pricing-page">
            <section className="border-b border-[#d8dcdd]">
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
                        <div className="lg:col-span-7">
                            <div className="tc-eyebrow text-[#01696f] mb-5">
                                Pricing · monthly · per user
                            </div>
                            <h1
                                data-testid="pricing-page-headline"
                                className="text-[32px] sm:text-[40px] lg:text-[46px] font-medium tracking-[-0.02em] text-[#0e1416] leading-[1.08]"
                            >
                                Three tiers.{" "}
                                <span className="text-[#5a6669]">
                                    One suite.
                                </span>
                            </h1>
                            <p className="mt-6 text-[15px] text-[#5a6669] leading-[1.65] max-w-[58ch]">
                                Start with Demo to evaluate the suite, scale to
                                Starter for production pours with the full
                                thermal + maturity workflow, and unlock live
                                monitoring and inverse calibration on
                                Professional. No re-implementation, no data
                                migration.
                            </p>
                        </div>
                        <div className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-[#d8dcdd] flex flex-col gap-5">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex flex-col">
                                    <span className="tc-mono text-[10.5px] text-[#5a6669] tracking-widest uppercase">
                                        Region
                                    </span>
                                    <span
                                        data-testid="pricing-region-label"
                                        className="mt-1 text-[13px] text-[#0e1416]"
                                    >
                                        {regionData.label}
                                    </span>
                                </div>
                                <div data-testid="pricing-region-picker">
                                    <RegionPicker placement="nav" />
                                </div>
                            </div>
                            <SpecLine
                                left="Billing"
                                right={`${currency} · monthly, per user`}
                            />
                            <SpecLine
                                left="Trial"
                                right="Demo plan, indefinite"
                            />
                            <SpecLine
                                left="Enterprise"
                                right="On-prem and team plans available"
                            />
                            <SpecLine
                                left="Payments"
                                right="Airwallex Hosted Checkout (Phase 2)"
                            />
                        </div>
                    </div>

                    <PricingCards idAttr="page" />

                    <p
                        data-testid="pricing-page-currency-caption"
                        className="mt-5 tc-mono text-[11px] text-[#5a6669] tracking-wider leading-[1.55]"
                    >
                        {caption}
                    </p>
                </div>
            </section>

            {/* FAQ-ish strip */}
            <section className="border-b border-[#d8dcdd] bg-[#eef0ef]">
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-16 lg:py-20">
                    <div className="flex items-baseline gap-6 mb-10">
                        <span className="tc-section-index">06</span>
                        <div className="flex-1 h-px bg-[#d8dcdd]" />
                        <h2 className="text-[22px] sm:text-[26px] font-medium tracking-tight text-[#0e1416]">
                            What&apos;s included
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        <IncludeColumn
                            title="Solvers"
                            rows={[
                                ["1D FDM thermal", "Demo · Starter · Pro"],
                                ["2D FEM thermal", "Starter · Pro"],
                                ["Inverse calibration", "Pro"],
                            ]}
                        />
                        <IncludeColumn
                            title="Maturity & strength"
                            rows={[
                                ["Nurse-Saul", "All tiers"],
                                ["Arrhenius / t_e", "Starter · Pro"],
                                ["Lab calibration", "Starter · Pro"],
                                ["Field calibration", "Pro"],
                            ]}
                        />
                        <IncludeColumn
                            title="Monitoring"
                            rows={[
                                ["CSV import", "Starter · Pro"],
                                ["Live API feed", "Pro"],
                                ["E_a calibration (live)", "Pro"],
                                ["Next-pour feedback", "Pro"],
                            ]}
                        />
                    </div>
                </div>
            </section>
        </PageShell>
    );
}

const SpecLine = ({ left, right }) => (
    <div className="flex items-baseline justify-between py-2.5 border-b border-[#d8dcdd] last:border-b-0">
        <span className="tc-mono text-[12px] text-[#0e1416]">{left}</span>
        <span className="text-[12.5px] text-[#5a6669] text-right max-w-[60%]">
            {right}
        </span>
    </div>
);

const IncludeColumn = ({ title, rows }) => (
    <div className="flex flex-col gap-4">
        <div className="tc-eyebrow text-[#0e1416]">{title}</div>
        <div className="border-t border-[#d8dcdd]">
            {rows.map(([k, v]) => (
                <div
                    key={k}
                    className="flex items-baseline justify-between py-3 border-b border-[#d8dcdd]"
                >
                    <span className="text-[13px] text-[#0e1416]">{k}</span>
                    <span className="tc-mono text-[11px] text-[#5a6669]">
                        {v}
                    </span>
                </div>
            ))}
        </div>
    </div>
);
